"use client";

// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
import { ctaClick } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import { ArrowUpRight, Check } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useCallback, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";

export interface LifeMilestone3MetadataRow {
  id?: string | number;
  meta_description?: string;
  meta_priority?: number;
  meta_status?: number | boolean;
}

export interface LifeMilestone3Data {
  id?: string;
  status?: boolean;
  background_color?: string;
  block_main_title?: string;
  block_subtitle?: string;
  block_body?: string;
  block_cta_label?: string;
  block_cta_url?: string;
  /** Open lead popup vs navigate to block_cta_url */
  is_cta?: boolean;
  hero_image?: string;
  hero_image_mobile?: string;
  hero_image_alt?: string;
  /** Checklist rows with check icon */
  metadata?: LifeMilestone3MetadataRow[];
}

interface LifeMilestone3Props {
  data: LifeMilestone3Data | { data: LifeMilestone3Data };
}

function resolvePayload(
  data: LifeMilestone3Props["data"],
): LifeMilestone3Data | undefined {
  const anyData = data as { data?: LifeMilestone3Data };
  if (anyData?.data && typeof anyData.data === "object") {
    return anyData.data as LifeMilestone3Data;
  }
  return data as LifeMilestone3Data;
}

function isRowActive(row: LifeMilestone3MetadataRow): boolean {
  if (row.meta_status === false || row.meta_status === 0) return false;
  return true;
}

/** Next.js `Link`–safe path for internal CTAs */
function normalizeCtaHref(url: string): string {
  const h = url.trim();
  if (!h) return "";
  if (
    h.startsWith("http://") ||
    h.startsWith("https://") ||
    h.startsWith("/") ||
    h.startsWith("#") ||
    h.startsWith("mailto:") ||
    h.startsWith("tel:")
  ) {
    return h;
  }
  return `/${h.replace(/^\//, "")}`;
}

const LifeMilestone3 = ({ data }: LifeMilestone3Props) => {
  const dispatch = useDispatch();
  const sectionData = resolvePayload(data);
  const ref = useRef<HTMLElement>(null);

  const {
    status = true,
    background_color = "#291973",
    block_main_title = "",
    block_subtitle = "",
    block_body = "",
    block_cta_label = "",
    block_cta_url = "",
    hero_image = "",
    hero_image_mobile = "",
    hero_image_alt = "",
    metadata = [],
  } = sectionData || {};

  const checklist = useMemo(() => {
    const rows = Array.isArray(metadata) ? metadata.filter(isRowActive) : [];
    return [...rows]
      .sort((a, b) => (a.meta_priority ?? 0) - (b.meta_priority ?? 0))
      .map((row) => (row.meta_description ?? "").trim())
      .filter(Boolean);
  }, [metadata]);

  const rawSection = sectionData as Record<string, unknown> | undefined;
  const isCtaOn = isCmsShowFlagOn(rawSection?.is_cta ?? rawSection?.isCta, true);

  const trimmedCtaUrl = String(block_cta_url ?? "").trim();
  const ctaHrefForLink = trimmedCtaUrl ? normalizeCtaHref(trimmedCtaUrl) : "";
  const ctaOpensLeadPopup = isCtaOn || !ctaHrefForLink;
  const ctaIsExternal = /^https?:\/\//i.test(trimmedCtaUrl);

  const openLeadFromCta = useCallback(() => {
    ctaClick({
      section: "life_milestone_3",
      component: "block_cta",
      label: block_cta_label?.trim() || "cta",
      step: "1",
      entity: "life_milestone",
      parentEntity: "life_milestone_3",
    });
    dispatch(
      handleOpenLeaddrop({
        variant: LEAD_DROP_VARIANTS.REGISTER,
      }),
    );
  }, [block_cta_label, dispatch]);

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current && status) {
  //     FireWidgetViewedEvent(ref, "life_milestone_3");
  //   }
  // }, [status]);

  if (!status) return null;

  const hasCopy =
    block_main_title ||
    block_subtitle ||
    block_body ||
    block_cta_label ||
    checklist.length > 0;

  if (!hasCopy) return null;

  const ctaButtonClass =
    "inline-flex w-fit items-center gap-2 rounded-lg border border-white bg-transparent px-5 py-2.5 text-[16px] font-medium text-white transition-colors hover:bg-white/10";

  const ctaLabelTrack = (component: string) =>
    ctaClick({
      section: "life_milestone_3",
      component,
      label: block_cta_label?.trim() || "cta",
      step: "1",
      entity: "life_milestone",
      parentEntity: "life_milestone_3",
    });

  return (
    <section
      ref={ref}
      className={cn(
        "[&_*]:![font-family:Poppins,sans-serif]",
        // Literal min-[..]px variants, not sm/md/lg/xl: this theme remaps those
        // tokens (sm=1080, md=1230, lg=1430), which held the mobile layout at
        // 125% zoom on a laptop (~1150-1210px viewport). Matches LifeMilestone2.
        "w-full px-6 py-10 min-[640px]:px-8 min-[768px]:px-11 min-[768px]:py-[52px]",
      )}
      style={{
        backgroundColor: background_color,
        fontFamily: "Poppins, sans-serif",
      }}
    >
      <div className="mx-auto flex max-w-[1200px] flex-col items-center gap-10 min-[1024px]:flex-row min-[1024px]:items-center min-[1024px]:justify-between min-[1024px]:gap-10 min-[1280px]:gap-14">
        <div className="flex min-w-0 flex-1 flex-col items-center gap-9 text-center text-white min-[1024px]:items-start min-[1024px]:text-left">
          <div className="flex flex-col items-center gap-6 min-[768px]:gap-8 min-[1024px]:items-start">
            {block_main_title || block_subtitle ? (
              <div className="flex flex-col items-center gap-1 min-[768px]:gap-[6px] min-[1024px]:items-start">
                {block_main_title ? (
                  <h2 className="text-[28px] font-semibold leading-[1.1] text-white min-[640px]:text-[40px] min-[1024px]:text-[52px] min-[1024px]:leading-[1.08]">
                    {block_main_title}
                  </h2>
                ) : null}
                {block_subtitle ? (
                  <p className="text-[20px] font-semibold leading-[1.1] text-white min-[640px]:text-[26px] min-[1024px]:text-[32px]">
                    {block_subtitle}
                  </p>
                ) : null}
              </div>
            ) : null}
            {block_body ? (
              <p className="text-[16px] font-medium leading-snug text-white/95 min-[640px]:text-[17px] min-[1024px]:text-[18px] min-[1024px]:leading-[1.45]">
                {block_body}
              </p>
            ) : null}
          </div>

          {block_cta_label ? (
            ctaOpensLeadPopup ? (
              <button
                type="button"
                className={ctaButtonClass}
                onClick={openLeadFromCta}
              >
                {block_cta_label}
                <ArrowUpRight className="size-5 shrink-0" aria-hidden />
              </button>
            ) : ctaIsExternal ? (
              <a
                href={trimmedCtaUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={ctaButtonClass}
                onClick={() => ctaLabelTrack("block_cta_link")}
              >
                {block_cta_label}
                <ArrowUpRight className="size-5 shrink-0" aria-hidden />
              </a>
            ) : (
              <Link
                href={ctaHrefForLink}
                className={ctaButtonClass}
                onClick={() => ctaLabelTrack("block_cta_link")}
              >
                {block_cta_label}
                <ArrowUpRight className="size-5 shrink-0" aria-hidden />
              </Link>
            )
          ) : null}

          {checklist.length > 0 ? (
            <div className="flex flex-col gap-[7px]">
              {checklist.map((label, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 text-[16px] font-bold leading-tight text-white min-[768px]:text-[18px]"
                >
                  <span
                    className="inline-flex size-6 shrink-0 items-center justify-center rounded-[6px] bg-white"
                    aria-hidden
                  >
                    <Check
                      className="size-3.5"
                      strokeWidth={3}
                      style={{ color: background_color }}
                    />
                  </span>
                  <span className="flex-1 text-left">{label}</span>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        {/* Mobile hero */}
        {hero_image_mobile ? (
          <div className="relative mt-4 h-[420px] w-full overflow-hidden min-[1024px]:hidden">
            <Image
              src={hero_image_mobile}
              alt={hero_image_alt || "Life milestone"}
              fill
              className="object-contain object-center"
              sizes="100vw"
            />
          </div>
        ) : null}

        {/* Desktop hero */}
        {hero_image ? (
          <div className="relative hidden h-[602px] w-[55%] shrink-0 overflow-hidden min-[1024px]:block">
            <Image
              src={hero_image}
              alt={hero_image_alt || "Life milestone"}
              fill
              className="object-contain object-right"
              sizes="55vw"
            />
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default LifeMilestone3;
